/**
 * Reusable file upload handler for quote forms
 *
 * Usage:
 * initQuoteFileUpload({
 *   formId: 'quoteForm',
 *   submitBtnId: 'quoteSubmitBtn',
 *   prefix: 'quote',
 *   presignedUrlEndpoint: 'YOUR_API_ENDPOINT'
 * });
 */

function initQuoteFileUpload(config) {
  const PRESIGNED_URL_ENDPOINT =
    config.presignedUrlEndpoint || "YOUR_API_ENDPOINT_HERE";
  const PREFIX = config.prefix || "quote";

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
  ];

  const uploadedFiles = [];
  let activeUploads = 0;

  const fileInput = document.getElementById(`${PREFIX}FileInput`);
  const fileUploadArea = document.getElementById(`${PREFIX}FileUploadArea`);
  const fileList = document.getElementById(`${PREFIX}FileList`);
  const uploadStatus = document.getElementById(`${PREFIX}UploadStatus`);
  const form = document.getElementById(config.formId);
  const submitBtn = document.getElementById(config.submitBtnId);
  const attachmentUrlsInput = document.getElementById(
    `${PREFIX}AttachmentUrls`,
  );

  if (
    !fileInput ||
    !fileUploadArea ||
    !fileList ||
    !uploadStatus ||
    !form ||
    !submitBtn ||
    !attachmentUrlsInput
  ) {
    console.error(
      "[QuoteFileUpload] Required DOM elements not found. Skipping initialization.",
    );
    return;
  }

  // Prevent browser's native validation UI (we handle it ourselves)
  form.setAttribute("novalidate", "");

  // Inject success message after form
  const successMessage = document.createElement("div");
  successMessage.className = "FormSuccess";
  successMessage.innerHTML = `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="9 12 11 14 15 10"></polyline>
    </svg>
    <h3>Thank you!</h3>
    <p>We've received your quote request and will be in touch shortly.</p>
  `;
  form.after(successMessage);

  // ========================
  // File upload
  // ========================

  fileUploadArea.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  });

  fileUploadArea.addEventListener("dragover", (e) => {
    e.preventDefault();
    fileUploadArea.classList.add("drag-over");
  });

  fileUploadArea.addEventListener("dragleave", () => {
    fileUploadArea.classList.remove("drag-over");
  });

  fileUploadArea.addEventListener("drop", (e) => {
    e.preventDefault();
    fileUploadArea.classList.remove("drag-over");
    handleFiles(e.dataTransfer.files);
  });

  function handleFiles(files) {
    uploadStatus.textContent = "";
    uploadStatus.className = "UploadStatus";

    Array.from(files).forEach((file) => {
      const validation = validateFile(file);
      if (!validation.valid) {
        showUploadStatus(validation.error, "error");
        return;
      }
      uploadFile(file);
    });
  }

  function validateFile(file) {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `"${file.name}" is too large. Maximum size is 10MB.`,
      };
    }
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return {
        valid: false,
        error: `"${file.name}" is an unsupported file type. Allowed: PDF, DOC, DOCX, JPG, PNG, GIF.`,
      };
    }
    return { valid: true };
  }

  async function uploadFile(file) {
    const fileId = Date.now() + "-" + Math.random().toString(36).substr(2, 9);
    const fileItem = createFileItem(file, fileId);
    fileList.appendChild(fileItem);

    activeUploads++;
    updateSubmitButton();

    try {
      const urlResponse = await fetch(PRESIGNED_URL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        }),
      });

      if (!urlResponse.ok)
        throw new Error("Failed to get upload URL from server");

      const { uploadUrl, publicUrl } = await urlResponse.json();

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResponse.ok)
        throw new Error("Failed to upload file to storage");

      uploadedFiles.push({ id: fileId, name: file.name, url: publicUrl });
      updateAttachmentUrls();
      updateFileItemStatus(fileId, "success");
    } catch (error) {
      console.error("[QuoteFileUpload] Upload error:", error);
      showUploadStatus(
        `Failed to upload "${file.name}". ${error.message}`,
        "error",
      );
      updateFileItemStatus(fileId, "error");
      const failedItem = fileList.querySelector(`[data-file-id="${fileId}"]`);
      if (failedItem) setTimeout(() => failedItem.remove(), 3000);
    } finally {
      activeUploads--;
      updateSubmitButton();
    }
  }

  function createFileItem(file, fileId) {
    const div = document.createElement("div");
    div.className = "FileItem";
    div.dataset.fileId = fileId;
    div.innerHTML = `
      <div class="FileInfo">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
          <polyline points="13 2 13 9 20 9"></polyline>
        </svg>
        <div class="FileDetails">
          <div class="FileName">${escapeHtml(file.name)}</div>
          <div class="FileSize">${formatFileSize(file.size)}</div>
        </div>
      </div>
      <div class="FileActions">
        <span class="FileProgress">Uploading...</span>
        <button type="button" class="FileRemove" data-file-id="${fileId}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
    div.querySelector(".FileRemove").addEventListener("click", function () {
      removeFile(this.dataset.fileId);
    });
    return div;
  }

  function updateFileItemStatus(fileId, status) {
    const fileItem = fileList.querySelector(`[data-file-id="${fileId}"]`);
    if (!fileItem) return;
    const progress = fileItem.querySelector(".FileProgress");
    if (status === "success") {
      progress.textContent = "Uploaded ✓";
      progress.style.color = "#27ae60";
    } else if (status === "error") {
      progress.textContent = "Failed ✗";
      progress.style.color = "#e74c3c";
    }
  }

  function removeFile(fileId) {
    const index = uploadedFiles.findIndex((f) => f.id === fileId);
    if (index > -1) {
      uploadedFiles.splice(index, 1);
      updateAttachmentUrls();
    }
    const fileItem = fileList.querySelector(
      `.FileItem[data-file-id="${fileId}"]`,
    );
    if (fileItem) fileItem.remove();
  }

  function updateAttachmentUrls() {
    attachmentUrlsInput.value = uploadedFiles
      .map((f) => `${f.name}: ${f.url}`)
      .join("\n");
  }

  // ========================
  // Form validation
  // ========================

  // Clear field error on input
  form.addEventListener("input", (e) => {
    if (e.target.matches("input, textarea")) {
      clearFieldError(e.target);
    }
  });

  function validateForm() {
    let firstError = null;

    form
      .querySelectorAll("input[required], textarea[required]")
      .forEach((field) => {
        clearFieldError(field);

        if (!field.value.trim()) {
          showFieldError(field, "This field is required");
          if (!firstError) firstError = field;
        } else if (
          field.type === "email" &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)
        ) {
          showFieldError(field, "Please enter a valid email address");
          if (!firstError) firstError = field;
        }
      });

    if (firstError) {
      firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    return true;
  }

  function showFieldError(field, message) {
    field.classList.add("field-error");
    const error = document.createElement("span");
    error.className = "FieldError";
    error.textContent = message;
    field.after(error);
  }

  function clearFieldError(field) {
    field.classList.remove("field-error");
    const error = field.nextElementSibling;
    if (error && error.classList.contains("FieldError")) error.remove();
  }

  // ========================
  // Form submission
  // ========================

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    if (activeUploads > 0) {
      showUploadStatus(
        "Please wait for all files to finish uploading.",
        "error",
      );
      return;
    }

    if (!validateForm()) return;

    submitBtn.disabled = true;
    submitBtn.textContent = "Sending...";

    try {
      const formData = new FormData(form);
      const data = {};
      formData.forEach((value, key) => { data[key] = value; });

      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.text();

      console.log("wtf");
      console.log(responseData);
      console.log(form.action);

      if (!response.ok) throw new Error("Submission failed");

      form.style.display = "none";
      successMessage.style.display = "flex";
      successMessage.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (error) {
      console.error("[QuoteFileUpload] Submission error:", error);
      showUploadStatus(
        "Something went wrong. Please try again or contact us directly.",
        "error",
      );
      submitBtn.disabled = false;
      submitBtn.textContent = "Send";
    }
  });

  // ========================
  // Helpers
  // ========================

  function updateSubmitButton() {
    if (activeUploads > 0) {
      submitBtn.disabled = true;
      submitBtn.textContent = `Uploading ${activeUploads} file${activeUploads > 1 ? "s" : ""}...`;
    } else {
      submitBtn.disabled = false;
      submitBtn.textContent = "Send";
    }
  }

  function showUploadStatus(message, type) {
    uploadStatus.textContent = message;
    uploadStatus.className = `UploadStatus ${type}`;
    if (type === "error") {
      setTimeout(() => {
        uploadStatus.textContent = "";
        uploadStatus.className = "UploadStatus";
      }, 5000);
    }
  }

  function formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  console.log(`[QuoteFileUpload] Initialized for form: ${config.formId}`);
}

window.initQuoteFileUpload = initQuoteFileUpload;
